// 横滑首尾内距由蓝图声明，放在滚动容器内。
import { contentPadding } from '../layout';
/**
 * L2 collections — ItemCollection · SessionCollection · ItemCalendar. GENERIC, not domain: owning identity/CRUD/pending/
 * live-update is a CAPABILITY, and a capability every widget reuses is L2 (a
 * hundredth widget swaps bindings and a card spec, and adds zero components).
 * This is where the framework earns its keep: identity, CRUD, pending,
 * live-update, filtering and the empty states live HERE, once, so a blueprint
 * only declares a `card`.
 *
 * The identity split is the load-bearing distinction:
 *   ItemCollection    one cell = one FOLDED ITEM  → item_id, per-item CRUD
 *   SessionCollection one cell = one CAPTURE      → grid_id = session || photo
 *                    path, no item id, host removes the WHOLE grid
 * Getting this wrong is the bug class the registry exists to prevent, so the
 * two are different components rather than a flag.
 *
 * A grid lays out `props.card` — an inline component NODE — once per row under
 * an `item` binding scope, and maps the card's `activated` signal onto its own
 * identity-carrying signal. The card never learns what a row is; the grid never
 * learns what a row looks like.
 *
 * The interpreter supplies `renderTemplate` (item scope + signal routing +
 * inspect markers). `props.renderTemplate` is the seam for a surface that
 * renders a grid WITHOUT the interpreter — the studio's component previews and
 * graph miniatures — so a preview shows the real card instead of a placeholder.
 * A blueprint is JSON and can never set it.
 */
import { useEffect, useMemo } from 'react';
import { EmptyState, PendingCard } from './feedback';
import { longPressHandlers, gapValue, fmt, useSelection, resetButton, hitTarget, widgetHoldsNothing } from './ui';
import { getPath, isTerminal } from '../paths';
import { isoDay, localDay } from '../derives';
import { UnsupportedComponent } from './Unsupported';

/**
 * THE BUSY LANES the host may report, and the two shapes each of them takes.
 *
 * The host publishes in-flight deletes under whichever key the surface that
 * asked for them owns — `deleting` for items, `deleting_grids` for captures,
 * `deleting_entries` / `deleting_photos` for the two older feeds — and each
 * entry is EITHER a bare handle or an object carrying one
 * (`{entry_id}` / `{grid_id}` / `{id}`). The collection used to read exactly
 * one key in exactly one shape, so a delete reported in any other dialect left
 * the cell at full opacity: the row sat there looking untouched until the next
 * fetch removed it, and the user tapped delete again.
 *
 * Reading all four, in both shapes, costs nothing and is the difference between
 * "the delete is happening" and "nothing happened".
 */
const DELETING_KEYS = ['deleting', 'deleting_grids', 'deleting_entries', 'deleting_photos'];
const UPDATING_KEYS = ['updating', 'updates'];

/** A pending entry → the handle it names, whichever shape it arrived in. */
function pendingHandle(v) {
  if (v == null) return null;
  if (typeof v === 'string' || typeof v === 'number') return String(v);
  if (typeof v !== 'object') return null;
  const h = v.entry_id ?? v.grid_id ?? v.item_id ?? v.id ?? v.session_id ?? null;
  return h == null ? null : String(h);
}

function pendingSet(ctx, keys) {
  const out = new Set();
  const lanes = ctx?.pending || {};
  for (const key of keys) {
    const list = lanes[key];
    if (!Array.isArray(list)) continue;
    for (const v of list) {
      const h = pendingHandle(v);
      if (h) out.add(h);
    }
  }
  return out;
}

/**
 * `busyLabels: {updating, deleting}` — the WORD over a cell that is mid-flight.
 *
 * Half-opacity says "something", and a user who just long-pressed delete cannot
 * tell it from "this row is disabled". The label says which, and it is the
 * blueprint's copy because only the blueprint knows what a row of THIS widget
 * is ("Removing dose…", "Updating outfit…").
 */
function BusyVeil({ label }) {
  if (!label) return null;
  return (
    <div style={{
      position: 'absolute', inset: 0, zIndex: 3, pointerEvents: 'none',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      borderRadius: 'var(--r)', background: 'color-mix(in srgb, var(--bg) 62%, transparent)',
    }}>
      <span style={{
        padding: '2px 10px', borderRadius: 'var(--r-pill)',
        background: 'var(--surface)', boxShadow: 'var(--shadow-1)',
        fontSize: 'var(--t-xs)', fontWeight: 700, color: 'var(--text-2)',
      }}>{label}</span>
    </div>
  );
}

/**
 * `texture: "dots"` — a dotted backdrop UNDER a collection's cells.
 *
 * The one decoration the kit sanctions, and only because it carries a reading:
 * a shelf drawn on a dotted field says "this is a surface things sit on, and it
 * has room for more" — which is precisely what an authored board or a
 * half-filled grid needs to say and what a bare page background does not.
 */
const TEXTURE = {
  dots: {
    backgroundImage: 'radial-gradient(var(--line) 1px, transparent 1px)',
    backgroundSize: '14px 14px',
    borderRadius: 'var(--r)',
    padding: 'var(--sp-3)',
  },
};

/**
 * `+4 more` — the overflow cell's copy.
 *
 * The blueprint supplies only the NOUN ("more", "more templates") and the
 * component owns the number, because a `{n}`-style placeholder cannot survive
 * the trip: `{…}` in a string prop is the BLUEPRINT's data channel and
 * `resolveProps` interpolates it against the page context long before this
 * component runs, so `"+{n} more"` would arrive as `"+ more"`. One brace
 * grammar, one owner — and composing from parts happens to be exactly prod's
 * copy anyway.
 */
function overflowCopy(label, remaining) {
  const noun = String(label ?? '').trim();
  return noun ? `+${remaining} ${noun}` : `+${remaining}`;
}

/** The right-hand reading of one group header (`groupMeta`, see the caller). */
function groupMeta(bucket, props) {
  const mode = props.groupMeta;
  if (mode === 'note') {
    const note = bucket.note ?? bucket.meta ?? '';
    return note === '' || note == null ? '' : String(note);
  }
  if (mode === 'subtotal') {
    return bucket.subtotal == null
      ? ''
      : `${fmt(bucket.subtotal)}${props.subtotalUnit ? ` ${props.subtotalUnit}` : ''}`;
  }
  return bucket.subtotal != null
    ? `${fmt(bucket.subtotal)}${props.subtotalUnit ? ` ${props.subtotalUnit}` : ''}`
    : fmt((bucket.items || []).length);
}

/** Rows the bind produced, in every shape a blueprint may hand a grid. */
function rowsFrom(data, ctx) {
  if (!data) return [];
  if (Array.isArray(data)) {
    if (!data.length) return [];
    // an id list (`focusEntry.garment_ids`) → resolve to folded rows
    if (typeof data[0] === 'string') return ctx?.resolveItems?.(data) || [];
    // a bucket list (a `group` selector) rendered flat → flatten
    if (Array.isArray(data[0]?.items)) return data.flatMap((b) => b.items || []);
    return data;
  }
  if (Array.isArray(data.groups)) return data.groups.flatMap((b) => b.items || []);
  return [];
}

function bucketsFrom(data) {
  if (Array.isArray(data) && data.length && Array.isArray(data[0]?.items)) return data;
  if (data && Array.isArray(data.groups)) return data.groups;
  return null;
}

/**
 * Substring match. With no declared `fields` every string the row carries is
 * searchable — a generic grid cannot know which field a widget calls "name".
 */
function matchesQuery(row, query, fields) {
  if (!query) return true;
  const q = String(query).toLowerCase().trim();
  if (!q) return true;
  const values = fields && fields.length
    ? fields.map((k) => getPath(row, k))
    : Object.values(row || {});
  return values.some((v) => {
    if (v == null) return false;
    if (Array.isArray(v)) return v.some((x) => typeof x === 'string' && x.toLowerCase().includes(q));
    if (typeof v !== 'string' && typeof v !== 'number') return false;
    return String(v).toLowerCase().includes(q);
  });
}

function isActive(v) {
  return v != null && v !== '' && v !== 'all' && !(Array.isArray(v) && !v.length);
}

/** Every value on a row, flattened — the facets a filter key can match. */
function rowValues(row) {
  const out = [];
  for (const v of Object.values(row || {})) {
    if (v == null) continue;
    if (Array.isArray(v)) out.push(...v.filter((x) => typeof x === 'string' || typeof x === 'number'));
    else if (typeof v === 'string' || typeof v === 'number') out.push(v);
  }
  return out.map((x) => String(x).toLowerCase());
}

/**
 * ONE filter dimension. Two forms, both generic:
 *   { field, value }  an explicit field match, when the source knows the field
 *   { key } / { field: value }  a VALUE the row must carry somewhere — the
 *     form a chip bar and a stat tile emit, since neither knows (or should
 *     know) which column of the row its key lives in.
 */
function filterOf(payload, fallbackField) {
  if (!payload || typeof payload !== 'object') return null;
  if (payload.value !== undefined || payload.field) {
    const value = payload.value !== undefined ? payload.value : payload[payload.field];
    return isActive(value) ? { field: payload.field || fallbackField || null, value } : null;
  }
  if (payload.key !== undefined) {
    return isActive(payload.key) ? { field: fallbackField || null, value: payload.key } : null;
  }
  // `{ date: "2026-08-01" }` — a single-entry payload names its own field.
  const keys = Object.keys(payload);
  if (keys.length === 1) {
    const value = payload[keys[0]];
    return isActive(value) ? { field: keys[0], value } : null;
  }
  return null;
}

/**
 * The filter dimensions a grid is currently under. Read STRAIGHT from the slot
 * inbox rather than mirrored into state: a slot payload is already the single
 * source of truth, and mirroring it is how a live-update push used to reset a
 * filter the user had chosen.
 *
 * Dimensions COMBINE: every source wired to `applyFilter` is its own dimension
 * (the inbox keeps one payload per source), and the grid ANDs them. That is
 * the whole reason a widget needs no per-dimension slot — a fourth chip bar is
 * one more connection, not one more component prop.
 */
function activeFilters(props, slots) {
  const slot = slots?.applyFilter;
  const payloads = slot ? (slot.bySource ? Object.values(slot.bySource) : [slot.payload]) : [];
  const out = payloads.map((p) => filterOf(p, props.filterField)).filter(Boolean);
  // `pick` is the PROP twin of the applyFilter/applyGroup slots, for a
  // container that draws its own chips: it has no wire to deliver one through,
  // and a filter nobody can forget to connect cannot silently do nothing.
  const picked = props.filterField ? filterOf({ key: props.pick }, props.filterField) : null;
  if (picked) out.push(picked);
  return out;
}

/** AND over dimensions; a list value is an OR within its own dimension. */
function matchesFilters(row, filters) {
  for (const f of filters) {
    const list = (Array.isArray(f.value) ? f.value : [f.value]).map((v) => String(v).toLowerCase());
    if (!list.length) continue;
    const got = f.field
      ? [String(getPath(row, f.field) ?? '').toLowerCase()]
      : rowValues(row);
    if (!list.some((w) => got.includes(w))) return false;
  }
  return true;
}

/**
 * Pending adds that still belong on screen. Three sealed rules:
 *
 *  DEDUP — the instant a landed row carries the add's session_id the
 *  placeholder is DROPPED; waiting for the host's terminal pending-clear would
 *  show both for the whole window. A grid BOUND to the pending list (the
 *  logging strip) would otherwise draw every add twice — once as its own card
 *  and once as this placeholder — so the add's own OBJECT is checked against
 *  the rows first, and the client_ref/session_id handles after it.
 *
 *  SCOPE — an add the host tagged for another part of the page is NOT this
 *  grid's. `scope` is the grid's own `region` (or an explicit
 *  `props.pendingScope`, which may be a LIST when one grid serves several tags
 *  — five category tabs behind one grid), matched against the add's tag. That
 *  is what keeps a Create-look placeholder off the Photos grid instead of
 *  drawing one skeleton on every tab. An UNTAGGED add still belongs everywhere:
 *  the host said nothing about where it lands, and hiding it would lose the
 *  capture — the failure this rule exists to prevent, inverted.
 */
// eslint-disable-next-line react-refresh/only-export-components -- pure helper, exported for cardgap dedup unit test
export function livePending(ctx, rows, scope) {
  const adds = ctx?.pending?.adds || [];
  if (!adds.length) return [];
  const landed = new Set(
    rows.flatMap((r) => [r.source_session_id, r.session_id, r.client_ref, r.grid_id])
      .filter(Boolean).map(String),
  );
  const scopes = (Array.isArray(scope) ? scope : [scope]).filter((s) => s != null && s !== '').map(String);
  return adds.filter((a) => {
    // the same OBJECT is already a row: a collection BOUND to the pending lane
    // is drawing this add as its own cell, and the handle match below cannot
    // see an add the host minted with neither a client_ref nor a session_id
    if (rows.includes(a)) return false;
    // running-only (CONTRACT §13): a session that reached a TERMINAL outcome
    // (completed → real log row; stopped / failed / timeout → durable feed Card
    // with a top-right red "!", see cards.jsx) is no longer a pending cell. Drop
    // it here so the strip shows only sessions still in flight and no empty grid
    // cell is left behind. The Flutter twin filters `_buildLoggingStrip.live`.
    if (isTerminal(a)) return false;
    if (a.session_id && landed.has(String(a.session_id))) return false;
    if (a.client_ref && landed.has(String(a.client_ref))) return false;
    const tag = a.tab ?? a.scope ?? null;
    if (scopes.length && tag && !scopes.includes(String(tag))) return false;
    return true;
  });
}

/**
 * Which of a session collection's two activation signals a cell's tap IS.
 *
 * A LANDED capture has a destination of its own (a photo page, an item), and
 * that is `gridActivated`. A row that is the PENDING LANE's own add — one of
 * the objects `ctx.pending.adds` holds, which is every row of a collection
 * BOUND to `pending.adds` — has none yet: the only thing behind it is the
 * session, so its tap is `sessionActivated`, exactly as it is for a cell the
 * pending lane drew inside a feed grid.
 *
 * IDENTITY, not a key match: in the window where an add and the row it produced
 * are both on the feed they share a session_id, and that row is LANDED — its
 * tap must stay `gridActivated`.
 */
function activationOf(ctx, row) {
  const adds = ctx?.pending?.adds;
  return Array.isArray(adds) && adds.includes(row) ? 'sessionActivated' : 'gridActivated';
}

/**
 * The in-flight cells a collection draws from the PENDING LANE, one per add.
 *
 * `PendingCard` is pure View like `Card`, so the identity is attached HERE:
 * the cell's passthrough `activated` becomes the collection's
 * `sessionActivated {session_id}` — the signal a blueprint wires to
 * `host.openSession`, and the same one a collection BOUND to `pending.adds`
 * emits when it lays the very same cell out as its `card` template.
 */
function pendingNodeId(collectionId, add, index) {
  const identity = add?.session_id || add?.client_ref || `row-${index}`;
  return `${collectionId || 'collection'}__pending__${identity}`;
}

function PendingCells({ nodeId, pending, aspect, emit, layOut = null, cellStyle = null }) {
  return pending.map((a, i) => {
    const id = pendingNodeId(nodeId, a, i);
    const props = { shape: 'tile', hasImage: true, aspect: aspect || '3 / 4' };
    const onSignal = (signal, payload) => emit?.(
      signal === 'activated' ? 'sessionActivated' : signal,
      { session_id: a.session_id ?? null, ...payload },
    );
    // Use the interpreter's template path when it is available. Inspect mode
    // then gives this generated cell the same marker an authored card gets,
    // while inspect-off runtime pixels and signals stay the PendingCard's own.
    const cell = layOut?.(
      { type: 'PendingCard', variant: 'tile', bind: 'item', props },
      { item: a, key: id, onSignal },
    ) || (
      <PendingCard
        key={id}
        variant="tile"
        props={props}
        data={a}
        emit={onSignal}
      />
    );
    // A rail lays its cells out itself (flex-basis + snap), so a placeholder
    // needs the same box a landed card gets — otherwise the in-flight capture
    // is a sliver between two full-width cards.
    return cellStyle
      ? <div key={id} style={cellStyle}>{cell}</div>
      : <CollectionGridCell key={id}>{cell}</CollectionGridCell>;
  });
}

/**
 * The node id a laid-out card carries. It is the grid's own id plus the row's
 * identity, so the studio's inspect overlay can select ONE card and still
 * resolve which declared grid it came from (`id.split('__card__')[0]`).
 */
function cardNodeId(gridId, itemId) {
  return `${gridId || 'grid'}__card__${itemId}`;
}

function gridStyle(columns, gap) {
  return {
    display: 'grid',
    gridTemplateColumns: `repeat(${columns}, minmax(0,1fr))`,
    gap: gapValue(gap, 'var(--sp-3)'),
  };
}

/**
 * One real CSS-grid item, shared by landed and lifecycle cells. The grid row
 * establishes the tallest card footprint; every cell then exposes that full
 * height to its child without teaching either Card or PendingCard about grid
 * columns, gaps, or row placement.
 */
function CollectionGridCell({ children, style = {}, ...rest }) {
  return (
    <div
      data-vi-collection-cell="grid"
      {...rest}
      style={{ minWidth: 0, height: '100%', display: 'flex', alignItems: 'stretch', ...style }}
    >
      {children}
    </div>
  );
}

/**
 * LAYOUT is a PROP, not a component (report F1/F15). One collection lays the
 * same `card` out four ways — `grid` (the default), `list` (one column),
 * `group` (a section header per bucket) and `rail` (horizontal, its own scroll
 * offset). Two surfaces showing the same cell are ONE component in two
 * layouts; what a cell IS — a folded item, a capture session, an authored
 * option — is the axis that stays a separate component, because it is the
 * axis with a different lifecycle.
 *
 * CAPABILITY DEFAULTS follow the layout, because they follow the SURFACE: a
 * grid/list/group/calendar is the place a collection LIVES, so it owns CRUD
 * and shows what is in flight; a rail is a glance at rows that live somewhere
 * else, so it owns neither by default — a long-press delete the owning surface
 * has to honour, offered on a preview strip, is exactly the bug. A rail that
 * genuinely owns its rows says `crud: true` / `pending: true`.
 */
const OWNS_BY_DEFAULT = new Set(['grid', 'list', 'group', 'calendar']);

/**
 * WHAT A LONG PRESS SAYS. The signal is `removeRequested` on both kits and the
 * blueprint wires it to `host.itemOptions` — the host's options SHEET, never
 * `host.crudRemove`, which is the delete primitive that sheet's Delete calls.
 *
 * The row's `image_url` rides along because the sheet's SAVE tile is offered
 * exactly when it is present (`showWidgetRuntimeItemOptions`); Flutter's shared
 * cell has always carried it, and a web cell that dropped it gave the same
 * long press a smaller sheet on the two kits.
 */
function removePayload(row, id) {
  const url = row && typeof row === 'object' ? row.image_url : null;
  return { item_id: id, ...(typeof url === 'string' && url ? { image_url: url } : null) };
}

/** `crud` / `pending`: the blueprint's answer, else the layout's default. */
function capability(props, name, layout) {
  return props[name] === undefined ? OWNS_BY_DEFAULT.has(layout) : props[name] === true;
}

/**
 * RAIL — the horizontal layout, laying out the SAME `card` template the grid
 * layouts do, one per row, keeping its own scroll offset across a live update.
 *
 * The one shape `Grid` structurally cannot be: `Grid` seals `overflow-x` shut
 * (it may never overflow horizontally) and a `Carousel` is a paged slide track
 * over ONE subject. A rail of ~2.5 cards under a section heading is neither,
 * and it is the shape every "chapter of templates / previews / related things"
 * surface asks for.
 *
 * A rail over NOTHING draws nothing — never a heading over air.
 */
function Rail({
  nodeId, props = {}, rows, card, layOut, emit, idOf, onSignal,
  onRemove = null, pending = [], deleting = null, busy = null, overflow = 0,
}) {
  const width = props.width || '62%';
  const cellStyle = {
    flex: `0 0 ${typeof width === 'number' ? `${width}px` : width}`,
    minWidth: 0,
    scrollSnapAlign: 'start',
  };

  if (!rows.length && !pending.length) return null;

  return (
    <div
      onScroll={emit ? (e) => emit('scrolled', { offset: e.currentTarget.scrollLeft }) : undefined}
      style={{
        display: 'flex', gap: gapValue(props.gap, 'var(--sp-3)'),
        overflowX: 'auto', overflowY: 'hidden', scrollbarWidth: 'none',
        scrollSnapType: props.snap === false ? undefined : 'x proximity',
        WebkitOverflowScrolling: 'touch', ...contentPadding(props.contentPadding), paddingBottom: contentPadding(props.contentPadding).paddingBottom + 2,
        ...(TEXTURE[props.texture] || null),
      }}
    >
      {pending.length
        ? <PendingCells
          nodeId={nodeId} pending={pending} aspect={props.aspect} emit={emit}
          layOut={layOut} cellStyle={cellStyle}
        />
        : null}
      {rows.map((r, i) => {
        const id = idOf(r, i);
        const remove = onRemove?.(r, id);
        const label = busy?.(String(id));
        return (
          <div
            key={id}
            {...(remove ? longPressHandlers(remove) : {})}
            style={deleting?.has(String(id))
              ? { ...cellStyle, position: 'relative', opacity: 0.4, transition: 'opacity 180ms var(--ease)' }
              : { ...cellStyle, position: 'relative' }}
          >
            {layOut?.(card, {
              item: r,
              key: cardNodeId(nodeId, id),
              onSignal: onSignal(r, id, i),
            }) || <UnsupportedComponent what="card template" name="card" detail="the rail declares no `card` node" />}
            <BusyVeil label={label} />
          </div>
        );
      })}
      {overflow > 0 && props.overflowLabel
        ? <OverflowCell label={props.overflowLabel} remaining={overflow} emit={emit} style={cellStyle} />
        : null}
    </div>
  );
}

/**
 * `limit` + `overflowLabel` — the CAP and the way out of it.
 *
 * A rail's job is a glance, and a glance does not have 40 cells: past the first
 * handful the user is scrolling a list inside a shelf, which is the shape the
 * page below the shelf already is. `limit` stops at the glance and the overflow
 * cell is where the rest lives — a real affordance emitting `overflowActivated
 * {remaining}`, so the blueprint decides whether that is a page, a tab or a
 * filter. Without `overflowLabel` the cap is silent: some rails genuinely mean
 * "the top three", and inventing a "+37 more" for them would be a lie about
 * what the shelf is.
 */
function OverflowCell({ label, remaining, emit, style }) {
  return (
    <button
      type="button"
      onClick={() => emit?.('overflowActivated', { remaining })}
      style={{
        ...resetButton, ...style, ...hitTarget,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        borderRadius: 'var(--r)', border: '1px dashed var(--line)',
        color: 'var(--accent)', fontSize: 'var(--t-sm)', fontWeight: 700,
        textAlign: 'center', padding: 'var(--sp-3)', minWidth: 0,
      }}
    >{overflowCopy(label, remaining)}</button>
  );
}

/**
 * `pageSize` + `pagerLabels` — client-side paging, and why it is not `limit`.
 *
 * `limit` says "this shelf shows the top N and the rest live elsewhere".
 * Paging says "all of it is here, a screenful at a time" — a different promise,
 * and the right one for a long OWNED list where the page below has nowhere else
 * to send the reader. They are mutually exclusive by construction: a capped
 * shelf has no second page, so `limit` wins when a blueprint declares both.
 *
 * The component owns the two NUMBERS and the blueprint owns the three words:
 * `prev` · `next` · `of` (the separator, default `/`), so "2 / 5" and
 * "2 of 5" are both sayable without a `{page}`-style placeholder — which could
 * not survive `resolveProps` anyway (see `overflowCopy`).
 */
function Pager({ page, pages, labels, onPage }) {
  if (pages <= 1) return null;
  const copy = labels && typeof labels === 'object' ? labels : {};
  const status = `${page + 1} ${copy.of || '/'} ${pages}`;
  const step = (delta) => onPage(Math.max(0, Math.min(pages - 1, page + delta)));
  const button = (delta, label, disabled) => (
    <button
      type="button"
      disabled={disabled}
      aria-label={label}
      onClick={() => step(delta)}
      style={{
        ...resetButton, ...hitTarget,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        color: disabled ? 'var(--text-3)' : 'var(--text)',
        opacity: disabled ? 0.4 : 1, fontSize: 'var(--t-sm)', fontWeight: 700,
        cursor: disabled ? 'default' : 'pointer',
      }}
    >{label}</button>
  );
  return (
    <div style={{
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      gap: 'var(--sp-3)', paddingTop: 'var(--sp-2)',
    }}>
      {button(-1, copy.prev || '‹', page === 0)}
      <span style={{ fontSize: 'var(--t-xs)', color: 'var(--text-3)', fontWeight: 700 }}>{status}</span>
      {button(1, copy.next || '›', page >= pages - 1)}
    </div>
  );
}

/** The dashed ghosts an empty bucket keeps, so the user learns it can fill. */
function BucketOutline({ count, aspect }) {
  return Array.from({ length: count }).map((_, i) => (
    <div key={`o-${i}`} style={{ aspectRatio: (aspect || '1 / 1').replace('/', ' / '), borderRadius: 'var(--r)', border: '1px dashed var(--line)' }} />
  ));
}

/**
 * SEALED: item identity · widget scope · pending dedup · the full CRUD state
 * machine · delete refresh · combined filters · a11y. A blueprint overrides
 * ONLY columns, aspect, gap, the card template, group mode and the empty copy.
 *
 * Three LAYOUT/BEHAVIOUR widenings, all additive:
 *   `list`        one column of rows — the same cards, stacked. A layout, not
 *                 a second component: a list and a grid differ by column count.
 *   `rail`        the same cards read SIDEWAYS, with the rail's own scroll
 *                 offset preserved. Also a layout, for the same reason — and
 *                 the one where `crud`/`pending` default OFF (see `capability`).
 *   `selectable`  a tap SELECTS the row in place (`itemSelected {item_id}` +
 *                 the `setSelected` slot) instead of opening it. This is what
 *                 the L4 Selection→Session recipe presumes and what a versions
 *                 strip needs: activating a cell must retarget the rest of the
 *                 page, never navigate away from it.
 *
 * …and five VOLUME/STATE props, all of them things a blueprint was hand-rolling
 * or doing without (see each helper for the argument):
 *   `limit` + `overflowLabel`   the glance, and the way out of it
 *   `pageSize` + `pagerLabels`  all of it, a screenful at a time
 *   `busyLabels {updating,deleting}`  the WORD over an in-flight cell
 *   `texture: "dots"`           the surface a shelf sits on
 *   `groupMeta: "note"|"subtotal"`  which reading rides a group header
 *   `hideEmptyGroups`           whether an emptied bucket is drawn at all
 * plus pending-key/shape tolerance (`pendingSet`), which is a bug fix wearing a
 * prop's clothes: a delete the host reported under any of its four lane names
 * used to leave the cell looking untouched.
 */
export function ItemCollection({ nodeId, node, variant, props = {}, data, ctx, emit, slots, renderTemplate }) {
  const query = slots?.applyQuery?.payload?.query ?? '';
  const selectable = props.selectable === true || variant === 'selectable';
  const listLayout = props.layout === 'list' || variant === 'list';
  const railLayout = props.layout === 'rail' || variant === 'rail';
  const [selected, setSelected] = useSelection(`${nodeId}__selected`, props.selected ?? null);
  const setSelectedSlot = slots?.setSelected;
  // A pushed selection SEEDS the local one rather than overriding it forever:
  // another module can say which row the page is about, and the user can still
  // pick a different one afterwards.
  useEffect(() => {
    if (setSelectedSlot?.payload?.item_id !== undefined) setSelected(setSelectedSlot.payload.item_id);
  }, [setSelectedSlot, setSelected]);
  const filters = useMemo(() => activeFilters(props, slots), [props, slots]);
  // `applyGroup` picks ONE bucket (hiding the others whole); the legacy
  // `setSnapshot {tab|key}` form does the same for blueprints wired that way.
  const pickedSlot = slots?.applyGroup?.payload?.key
    ?? slots?.setSnapshot?.payload?.tab ?? slots?.setSnapshot?.payload?.key
    ?? props.pick ?? null;
  // `layout` is the ONE channel (report F15). `props.group` is the older bool
  // that said the same thing and still answers, so a hand-written node keeps
  // working; `variant` is the third and is only read for a node authored
  // before layout moved onto the props.
  const grouped = props.layout === 'group' || Boolean(props.group) || variant === 'group';

  const { buckets, bucketId } = useMemo(() => {
    const explicit = slots?.setSnapshot?.payload?.entries;
    // `props.items` is the LITERAL source a rail is authored with; a bound
    // collection uses `bind` → `data`.
    const source = Array.isArray(explicit)
      ? explicit
      : (props.items !== undefined ? props.items : data);
    const raw = bucketsFrom(source);
    const ids = new Set((raw || []).map((b) => String(b.id ?? b.key)));

    // A filter dimension whose value NAMES a bucket selects that bucket whole
    // rather than filtering rows: hiding a group must hide the SHELF, never
    // leave an empty header behind. Same slot, same payload — the grid just
    // knows which of its two axes the key belongs to.
    const bucketPick = filters.find((f) => !f.field && !Array.isArray(f.value) && ids.has(String(f.value)));
    const rowFilters = bucketPick ? filters.filter((f) => f !== bucketPick) : filters;
    const active = isActive(pickedSlot) ? pickedSlot : (bucketPick ? bucketPick.value : null);
    const keep = (r) => matchesQuery(r, query, props.fields) && matchesFilters(r, rowFilters);

    if (grouped && raw) {
      return {
        bucketId: active,
        buckets: raw
          .filter((b) => !active || String(b.id ?? b.key) === String(active))
          .map((b) => ({ ...b, id: b.id ?? b.key, items: (b.items || []).filter(keep) })),
      };
    }
    let flat;
    if (raw) {
      const picked = active ? raw.find((b) => String(b.id ?? b.key) === String(active)) : null;
      flat = picked ? (picked.items || []) : (active ? [] : raw.flatMap((b) => b.items || []));
    } else {
      flat = rowsFrom(source, ctx);
    }
    return { bucketId: active, buckets: [{ id: null, label: '', items: flat.filter(keep) }] };
  }, [data, slots, grouped, pickedSlot, query, filters, props.fields, props.items, ctx]);

  const rows = useMemo(() => buckets.flatMap((b) => b.items), [buckets]);
  const layout = railLayout ? 'rail' : (listLayout ? 'list' : (grouped ? 'group' : 'grid'));
  const owns = capability(props, 'crud', layout);
  const showsPending = capability(props, 'pending', layout);
  const pending = showsPending
    ? livePending(ctx, rows, props.pendingScope ?? node?.region ?? null)
    : [];
  const columns = listLayout ? 1 : (Number(props.columns) || 2);
  const deleting = pendingSet(ctx, DELETING_KEYS);
  const updating = pendingSet(ctx, UPDATING_KEYS);
  // The KIT says a word by default; `busyLabels` only OVERRIDES it with domain copy.
  // Leaving the word to the blueprint meant no word at all: seven of the nine shipped
  // widgets declare none, so a long-press delete showed a 40%-dim tile and nothing
  // else — and a delete can run for SEVERAL SECONDS, which the user spends wondering
  // whether the tap landed. Same default in both kits (the Flutter twin), like every
  // other blueprint-overridable string here ("Nothing here yet", "No photos yet").
  const busyLabels = props.busyLabels && typeof props.busyLabels === 'object' ? props.busyLabels : null;
  const busyLabel = (id) => (
    deleting.has(id)
      ? (busyLabels?.deleting ?? 'Deleting…')
      : (updating.has(id) ? (busyLabels?.updating ?? 'Updating…') : null)
  ) || null;
  const card = props.card;
  const dividedRows = listLayout && props.divided === true;
  const layOut = renderTemplate || props.renderTemplate;
  const rowId = (r, i) => r.item_id || r.id || r.entry_id || `row-${i}`;

  // `limit` CAPS what a surface shows (see `OverflowCell`); `pageSize` walks
  // all of it a screenful at a time (see `Pager`). They are different promises
  // and a shelf cannot make both, so a declared `limit` wins.
  const limit = Number(props.limit) > 0 ? Number(props.limit) : 0;
  const pageSize = !limit && Number(props.pageSize) > 0 ? Number(props.pageSize) : 0;
  const [page, setPage] = useSelection(`${nodeId}__page`, 0);
  const capped = (items) => (limit ? items.slice(0, limit) : items);

  // A RAIL is the same cards read sideways, so it short-circuits the grid's
  // bucket furniture (section headers, ghost outlines, the two empties) and
  // draws nothing at all when there is nothing to draw.
  if (railLayout) {
    return (
      <Rail
        nodeId={nodeId} props={props} rows={capped(rows)} card={card} layOut={layOut} emit={emit}
        pending={pending} deleting={owns ? deleting : null} busy={busyLabel}
        overflow={limit ? Math.max(0, rows.length - limit) : 0}
        idOf={rowId}
        onRemove={owns ? ((r, id) => () => emit?.('removeRequested', removePayload(r, id))) : null}
        onSignal={(r, id, i) => (signal, payload, rowCtx) => {
          if (selectable && signal === 'activated') setSelected(id);
          return emit?.(
            selectable && signal === 'activated' ? 'itemSelected'
              : (signal === 'activated' ? 'itemActivated' : signal),
            { item_id: id, index: i, ...payload },
            rowCtx,
          );
        }}
      />
    );
  }

  if (!rows.length && !pending.length) {
    // SEALED: first-run and filter-miss are DIFFERENT empties. A search that
    // matched nothing is never "add your first item" — the user already has
    // items, they just filtered them away.
    const filtering = Boolean(query) || Boolean(bucketId) || filters.length > 0;
    const kind = filtering ? 'filter-miss' : (props.emptyKind || 'first-run');
    // ONE first run per page (see `pageOwnsFirstRun`, interpreter.jsx). When the
    // page declares its own `region:"empty"` first-run/onboarding state and the
    // widget genuinely holds nothing, THAT state is the answer — this grid
    // saying "Nothing here yet" above it answers the same question twice and
    // buries the half that teaches. The moment the widget holds any row (or an
    // in-flight capture) the page-level empty suppresses itself instead, and
    // this grid's own first-run copy is the only one left: an empty TAB beside
    // a stocked one still reads exactly as it did.
    if (kind === 'first-run' && ctx?.__pageFirstRun && widgetHoldsNothing(ctx)) return null;
    return (
      <EmptyState
        variant={kind}
        props={{
          kind,
          title: filtering ? (props.filterEmptyTitle || 'Nothing matches') : (props.emptyTitle || 'Nothing here yet'),
          body: filtering ? (props.filterEmptyBody || 'Clear the search or pick another filter.') : props.emptyBody,
        }}
        ctx={ctx}
        emit={emit}
      />
    );
  }

  const cells = (items, { withPending = false, outline = false, overflow = 0 } = {}) => (
    <div style={{ ...gridStyle(columns, props.gap), ...(TEXTURE[props.texture] || null) }}>
      {withPending ? (
        <PendingCells
          nodeId={nodeId} pending={pending} aspect={props.aspect} emit={emit} layOut={layOut}
        />
      ) : null}
      {items.map((r, rowIndex) => {
        const id = r.item_id || r.id || r.entry_id;
        return (
          <CollectionGridCell
            key={id}
            data-selected={selectable && String(selected) === String(id) ? '1' : undefined}
            {...(owns ? longPressHandlers(() => emit?.('removeRequested', removePayload(r, id))) : {})}
            style={{
              position: 'relative',
              // The hairline a LIST of rows reads by. A grid never gets one:
              // its gutters already say where one cell ends.
              borderTop: dividedRows && rowIndex > 0 ? '1px solid var(--line)' : undefined,
              paddingTop: dividedRows && rowIndex > 0 ? 'var(--sp-3)' : undefined,
              opacity: deleting.has(String(id)) ? 0.4 : 1,
              transition: 'opacity 180ms var(--ease)',
              // A hairline drawn on a ROUNDED box curls up at both ends. A
              // full-bleed list row has nothing to round anyway, so the two
              // readings never collide (Flutter draws a straight `Border.top`).
              borderRadius: dividedRows ? 0 : 'var(--r)',
              boxShadow: selectable && String(selected) === String(id)
                ? '0 0 0 2px var(--accent)' : undefined,
            }}
          >
            {layOut?.(card, {
              item: r,
              // grid-scoped + item-stable, so the studio's inspect overlay can
              // resolve which card was clicked back to the grid that owns it
              key: cardNodeId(nodeId, id),
              // the card's ROW SCOPE rides up with the signal, so a connection
              // on THIS grid can shape its payload from the activated row
              onSignal: (signal, payload, rowCtx) => {
                if (selectable && signal === 'activated') setSelected(id);
                return emit?.(
                  selectable && signal === 'activated' ? 'itemSelected'
                    : (signal === 'activated' ? 'itemActivated' : signal),
                  { item_id: id, ...payload },
                  rowCtx,
                );
              },
            }) || <UnsupportedComponent what="card template" name="card" detail="the grid declares no `card` node" />}
            <BusyVeil label={busyLabel?.(String(id))} />
          </CollectionGridCell>
        );
      })}
      {overflow > 0 && props.overflowLabel
        ? <OverflowCell label={props.overflowLabel} remaining={overflow} emit={emit} />
        : null}
      {outline && !items.length && props.emptyOutline !== 0
        ? <BucketOutline count={Number(props.emptyOutline) || 2} aspect={props.aspect} />
        : null}
    </div>
  );

  if (!grouped) {
    const all = buckets[0].items;
    if (pageSize) {
      const pages = Math.max(1, Math.ceil(all.length / pageSize));
      const at = Math.min(Number(page) || 0, pages - 1);
      return (
        <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
          {cells(all.slice(at * pageSize, at * pageSize + pageSize), { withPending: at === 0 })}
          <Pager page={at} pages={pages} labels={props.pagerLabels} onPage={setPage} />
        </div>
      );
    }
    return cells(capped(all), {
      withPending: true,
      overflow: limit ? Math.max(0, all.length - limit) : 0,
    });
  }

  // WHETHER AN EMPTY BUCKET IS DRAWN AT ALL is a fact about the widget, not
  // about the bucket — which is why it is a prop and why it defaults OFF: a
  // wardrobe's empty shelf says "you have none of these yet" and its ghost
  // outlines ARE that sentence, while a garden with nothing spotted should not
  // float a `Spotted` heading over air. Same reading as the Flutter kit
  // (`hideEmptyGroups`, canonical.dart), so the two runtimes draw the same page.
  const drawn = props.hideEmptyGroups ? buckets.filter((b) => b.items.length) : buckets;
  // Hiding every bucket must not also hide the IN-FLIGHT capture the grid was
  // still holding (the only way to get here with no rows at all): fall back to
  // the flat cells, which is what Flutter's `anyItems` fallback draws.
  if (!drawn.length) return cells([], { withPending: true });

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-5)' }}>
      {drawn.map((b, i) => (
        <section key={b.id ?? i} style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-2)' }}>
          {b.label ? (
            <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 8 }}>
              <h3 style={{
                margin: 0, fontSize: 'var(--t-md)', fontWeight: 700, letterSpacing: '-0.01em',
                fontFamily: 'var(--font-display)',
              }}>{b.label}</h3>
              {/* `groupMeta` picks WHICH reading rides the header, because the
                  right one is a fact about the widget, not about the bucket:
                  a meal shelf means its calorie SUBTOTAL, a plant shelf means
                  its NOTE ("water Thursday"), and the default — a subtotal if
                  the derive emitted one, otherwise the count — is what the
                  header always did. `subtotal` forced on a bucket without one
                  prints nothing rather than falling back to a count the author
                  deliberately did not ask for. */}
              <span style={{ fontSize: 'var(--t-xs)', color: 'var(--text-3)', fontWeight: 600 }}>
                {groupMeta(b, props)}
              </span>
            </div>
          ) : null}
          {/* the in-flight capture sits in the FIRST bucket, where the newest
              row lands once it settles; an emptied bucket keeps its ghosts so
              the user learns the shelf exists and can be filled */}
          {cells(capped(b.items), {
            withPending: i === 0,
            outline: true,
            overflow: limit ? Math.max(0, b.items.length - limit) : 0,
          })}
        </section>
      ))}
    </div>
  );
}

/**
 * SEALED: grid_id derivation (session_id || photo path) · instance-bound
 * pending · session-scoped remove · pending dedup. There is NO item id here —
 * a capture spans many items, so a per-item delete cannot express this grid.
 */
export function SessionCollection({ nodeId, node, variant, props = {}, data, ctx, emit, slots, renderTemplate }) {
  const explicit = slots?.setSnapshot?.payload?.entries;
  const source = Array.isArray(explicit)
    ? explicit
    : (props.items !== undefined ? props.items : data);
  // running-only (CONTRACT §13): a SessionCollection BOUND to the pending lane IS
  // the logging strip (the Flutter twin routes `bind.startsWith('pending')` to
  // `_buildLoggingStrip`). Drop terminal adds so the strip shows only sessions still
  // in flight — a terminal session's re-entry is the durable feed Card, not a strip
  // cell. A LANDED session grid (My Photos) is NOT pending-bound, so its rows (which
  // may legitimately carry a completed `status`) are untouched.
  const pendingBound = typeof node?.bind === 'string' && node.bind.startsWith('pending');
  const rows = (Array.isArray(explicit) ? explicit : rowsFrom(source, ctx))
    .filter((r) => !pendingBound || !isTerminal(r));
  const railLayout = props.layout === 'rail' || variant === 'rail';
  const layout = railLayout ? 'rail' : 'grid';
  const owns = capability(props, 'crud', layout);
  const pending = capability(props, 'pending', layout)
    ? livePending(ctx, rows, props.pendingScope ?? node?.region ?? null)
    : [];
  const columns = Number(props.columns) || 2;
  // the GRID lanes only — an item delete is not this surface's business — but
  // in every shape the host reports them (see `pendingSet`)
  const deletingGrids = pendingSet(ctx, ['deleting_grids', 'deleting_photos']);
  const card = props.card;
  const layOut = renderTemplate || props.renderTemplate;
  // grid_id = session || photo path, and — for a row that IS an in-flight add —
  // its client_ref, the only handle a not-yet-minted session has.
  const gridIdOf = (r, i) => r.grid_id || r.session_id || r.photo_url || r.client_ref || r.id || `row-${i}`;

  if (railLayout) {
    return (
      <Rail
        nodeId={nodeId} props={props} rows={rows} card={card} layOut={layOut} emit={emit}
        pending={pending} deleting={owns ? deletingGrids : null}
        idOf={gridIdOf}
        onRemove={owns
          ? ((r, id) => () => emit?.('removeRequested', { grid_id: id, session_id: r?.session_id ?? null }))
          : null}
        onSignal={(r, id, i) => (signal, payload, rowCtx) => emit?.(
          signal === 'activated' ? activationOf(ctx, r) : signal,
          { grid_id: id, session_id: r.session_id ?? null, index: i, ...payload },
          rowCtx,
        )}
      />
    );
  }

  if (!rows.length && !pending.length) {
    if (props.emptyKind === 'none') return null;
    // the same one-first-run-per-page rule the item grid obeys
    if (ctx?.__pageFirstRun && widgetHoldsNothing(ctx)) return null;
    return (
      <EmptyState
        variant="first-run"
        props={{ kind: 'first-run', title: props.emptyTitle || 'No captures yet', body: props.emptyBody }}
        ctx={ctx}
        emit={emit}
      />
    );
  }

  return (
    <div style={gridStyle(columns, props.gap)}>
      <PendingCells
        nodeId={nodeId} pending={pending} aspect={props.aspect} emit={emit} layOut={layOut}
      />
      {rows.map((r, i) => {
        // grid_id = session || photo path, and — for a row that IS an in-flight
        // add — its client_ref, which is the only handle a not-yet-minted
        // session has and the one the host's delete takes.
        const gridId = r.grid_id || r.session_id || r.photo_url || r.client_ref || r.id || `row-${i}`;
        // THE SAME GATE THE RAIL HAS (`onRemove` above). A long press is one
        // KIT ability — it opens the host's options sheet — so the surface
        // that offers it must be the same in both layouts; the grid used to
        // offer it unconditionally, which meant a `crud: false` session grid
        // still asked the host to remove rows it had disowned. Every shipped
        // blueprint's session grid owns its rows (grid layout ⇒ `crud`
        // defaults true), so this changes nothing they do — it closes the hole
        // under them. `session_id` rides along the way Flutter's cell sends
        // it, so the sheet's Delete resolves the same target on both kits.
        const remove = owns
          ? () => emit?.('removeRequested', { grid_id: gridId, session_id: r.session_id ?? null })
          : null;
        return (
          <CollectionGridCell
            key={gridId}
            {...(remove ? longPressHandlers(remove) : {})}
            style={{
              position: 'relative',  // the veil is absolutely positioned
              opacity: deletingGrids.has(String(gridId)) ? 0.4 : 1,
              transition: 'opacity 180ms var(--ease)',
            }}
          >
            {layOut?.(card, {
              item: r,
              key: cardNodeId(nodeId, gridId),
              onSignal: (signal, payload, rowCtx) => emit?.(
                signal === 'activated' ? activationOf(ctx, r) : signal,
                { grid_id: gridId, session_id: r.session_id ?? null, ...payload },
                rowCtx,
              ),
            }) || <UnsupportedComponent what="card template" name="card" detail="the grid declares no `card` node" />}
            {/* The WORD, same as the item grid. This surface only dimmed, so on device a
                session delete (My Photos) had no in-place reading at all and the host
                covered for it with a FULL-SCREEN spinner — a different answer to the same
                gesture than the item grid gave. `busyLabels.deleting` overrides. */}
            <BusyVeil label={deletingGrids.has(String(gridId))
              ? (props.busyLabels?.deleting ?? 'Deleting…')
              : null} />
          </CollectionGridCell>
        );
      })}
    </div>
  );
}

// ── ItemCalendar ──────────────────────────────────────────────────────
// Dates are LOCKED to en-US (the same rule the derives carry): the same feed
// must read identically on every device, whatever locale it is set to.
const DOW = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];
const MONTH_LABEL = { year: 'numeric', month: 'long' };

/** `YYYY-MM` of any date-ish value, or '' — the month a row belongs to. */
function monthOf(iso) {
  return typeof iso === 'string' && iso.length >= 7 ? iso.slice(0, 7) : '';
}

/** `2026-08-08` → `August 8` — the day, in the kit's one locked locale. */
function dayLabel(iso) {
  const [y, m, d] = String(iso).split('-').map((n) => parseInt(n, 10));
  if (!Number.isFinite(y) || !Number.isFinite(m) || !Number.isFinite(d)) return String(iso);
  return new Date(y, m - 1, d).toLocaleDateString('en-US', { month: 'long', day: 'numeric' });
}

/** `YYYY-MM` + a delta in months → `YYYY-MM`. */
function shiftMonth(key, delta) {
  const [y, m] = String(key).split('-').map((n) => parseInt(n, 10));
  if (!Number.isFinite(y) || !Number.isFinite(m)) return key;
  const d = new Date(y, (m - 1) + delta, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

/**
 * ItemCalendar — a month of day cells over a DATE-BUCKETED row list.
 *
 * Generic by construction: it is handed rows and the NAME of the field that
 * dates them (`dateField`, default "date"), counts them per day itself, and
 * knows nothing about what a row is. Cells carry three orthogonal readings —
 * today, has-rows, selected — so a day with content is legible before it is
 * chosen. Leading blanks are inert.
 *
 * With a `card` template it also lays out the SELECTED day's rows underneath,
 * which is what makes `ItemCollection` an honest fallback: the same rows under
 * the same card, listed instead of calendared.
 */
export function ItemCalendar({ nodeId, props = {}, data, ctx, emit, slots, renderTemplate }) {
  const rows = rowsFrom(props.items !== undefined ? props.items : data, ctx);
  const field = props.dateField || 'date';
  // LOCAL day, the same rule every derive buckets by (`localDay`). A raw
  // `.slice(0,10)` is the UTC day of a timestamp, which put an evening capture
  // one cell out from the very list it is supposed to index.
  const dayOfRow = (r) => localDay(getPath(r, field) ?? r?.created_at ?? '');

  const today = isoDay(new Date());
  const counts = new Map();
  for (const r of rows) {
    const d = dayOfRow(r);
    if (d) counts.set(d, (counts.get(d) || 0) + 1);
  }

  const seedDay = props.selected || (rows.length ? dayOfRow(rows[0]) : '') || today;
  const [shownMonth, setMonth] = useSelection(`${nodeId}__month`, monthOf(props.month || seedDay));
  const [selected, setPicked] = useSelection(`${nodeId}__day`, props.selected ?? null);
  const monthSlot = slots?.setMonth;
  const daySlot = slots?.setSelected;

  // Both slots SEED the local state (the chip-bar idiom) rather than shadowing
  // it — a pushed month that could not then be stepped would be a dead control.
  useEffect(() => {
    if (monthSlot?.payload?.month !== undefined) setMonth(monthOf(String(monthSlot.payload.month)));
  }, [monthSlot, setMonth]);
  useEffect(() => {
    if (daySlot?.payload?.date !== undefined) setPicked(daySlot.payload.date);
  }, [daySlot, setPicked]);

  const [y, m] = String(shownMonth).split('-').map((n) => parseInt(n, 10));
  const first = new Date(y || 1970, (m || 1) - 1, 1);
  const days = new Date(y || 1970, (m || 1), 0).getDate();
  const lead = first.getDay();

  const step = (delta) => {
    const next = shiftMonth(shownMonth, delta);
    setMonth(next);
    emit?.('monthChanged', { delta, month: next });
  };
  const pick = (date) => {
    setPicked(date);
    emit?.('daySelected', { date });
  };

  const cell = (date, label) => {
    const n = date ? (counts.get(date) || 0) : 0;
    const on = date && String(selected) === String(date);
    return (
      <button
        key={date || `blank-${label}`}
        type="button"
        disabled={!date}
        aria-pressed={Boolean(on)}
        aria-label={date ? `${date}${n ? `, ${n}` : ''}` : undefined}
        onClick={date ? () => pick(date) : undefined}
        style={{
          ...resetButton,
          aspectRatio: '1 / 1', borderRadius: 'var(--r-sm)',
          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 3,
          fontSize: 'var(--t-sm)', fontWeight: date === today ? 800 : 500,
          background: on ? 'var(--accent)' : (date === today ? 'var(--surface-3)' : 'transparent'),
          color: on ? 'var(--accent-ink)' : (date ? 'var(--text)' : 'transparent'),
          border: on ? '1px solid var(--accent)' : '1px solid transparent',
          cursor: date ? 'pointer' : 'default',
          minWidth: 0, minHeight: 0,
        }}
      >
        <span>{label}</span>
        <span style={{
          width: 5, height: 5, borderRadius: 3, flex: '0 0 auto',
          background: n ? (on ? 'var(--accent-ink)' : 'var(--accent)') : 'transparent',
        }} />
      </button>
    );
  };

  const cells = [];
  for (let i = 0; i < lead; i++) cells.push(cell(null, i));
  for (let d = 1; d <= days; d++) {
    cells.push(cell(`${shownMonth}-${String(d).padStart(2, '0')}`, d));
  }

  const dayRows = selected ? rows.filter((r) => dayOfRow(r) === String(selected)) : [];
  const layOut = renderTemplate || props.renderTemplate;
  const card = props.card || null;
  // THE SELECTED DAY, said out loud. The month grid answers "which day", and
  // the rows under it answer "what", and between them there was nothing naming
  // the thing the reader just tapped — so a day with two rows and a day with
  // none looked like the same screen with a different number of cards, and an
  // EMPTY day looked like the calendar had stopped working.
  //
  // `dayHeader` is the NOUN ("posts", "meals"); the date and the count are the
  // component's, so the line reads "August 8 · 2 posts". A `{count}`-style
  // placeholder is not available here — `{…}` is the blueprint's own data
  // channel and `resolveProps` empties it before this component runs.
  const dayHeader = selected && props.dayHeader
    ? `${dayLabel(String(selected))} · ${dayRows.length} ${String(props.dayHeader).trim()}`.trim()
    : null;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-3)' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 'var(--sp-2)' }}>
        <button type="button" aria-label="Previous month" onClick={() => step(-1)} style={{ ...resetButton, ...hitTarget, color: 'var(--text-2)', fontSize: 18 }}>‹</button>
        <div style={{ fontSize: 'var(--t-md)', fontWeight: 700, fontFamily: 'var(--font-display)' }}>
          {Number.isFinite(y) ? first.toLocaleDateString('en-US', MONTH_LABEL) : shownMonth}
        </div>
        <button type="button" aria-label="Next month" onClick={() => step(1)} style={{ ...resetButton, ...hitTarget, color: 'var(--text-2)', fontSize: 18 }}>›</button>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, minmax(0,1fr))', gap: 2 }}>
        {DOW.map((d, i) => (
          <div key={`dow-${i}`} style={{ textAlign: 'center', fontSize: 'var(--t-xs)', color: 'var(--text-3)', fontWeight: 700 }}>{d}</div>
        ))}
        {cells}
      </div>
      {dayHeader ? (
        <div style={{
          fontSize: 'var(--t-sm)', fontWeight: 700, color: 'var(--text-2)',
          fontFamily: 'var(--font-display)', letterSpacing: '-0.01em',
        }}>{dayHeader}</div>
      ) : null}
      {selected && !dayRows.length && props.dayEmptyLabel ? (
        <div style={{
          padding: 'var(--sp-4)', textAlign: 'center', borderRadius: 'var(--r)',
          background: 'var(--surface-2)', color: 'var(--text-3)', fontSize: 'var(--t-sm)',
        }}>{props.dayEmptyLabel}</div>
      ) : null}
      {card && dayRows.length ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-2)' }}>
          {dayRows.map((r, i) => {
            const id = r.item_id || r.id || r.entry_id || `row-${i}`;
            return (
              <div key={id}>
                {layOut?.(card, {
                  item: r,
                  key: cardNodeId(nodeId, id),
                  onSignal: (signal, payload, rowCtx) => emit?.(
                    signal === 'activated' ? 'itemActivated' : signal,
                    { item_id: id, date: selected, ...payload },
                    rowCtx,
                  ),
                })}
              </div>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}
